/**
 * MarketInsightDetail — 行业报告详情侧边栏
 *
 * 通过 useStore hook 与 MarketInsightCard 共享同一个 store 实例。
 * 按 block.type 线性渲染所有 blocks，无 Accordion 折叠。
 * 支持 Market Trend 和 Inspector（Company Audit）两种报告类型。
 *
 * Figma 参考：
 *   Market Trend: node 10320-24891
 *   Inspector:    node 10364-98757
 */

import { useCallback } from 'react';
import type { MarketInsightDetailProps, MarketInsightState } from './types';
import { MarketInsightDetailContent } from './MarketInsightDetailContent';

// ─────────────────────────────────────────────
// 派生字段 Hook
// ─────────────────────────────────────────────
export function useDetailFields(detail: MarketInsightState['detail']) {
  const title = detail?.title ?? '';
  const summary = detail?.summary ?? '';
  const reportType = (detail?.reportType ?? 1) as 1 | 2;
  const createdAt = detail?.createdAt ?? '';
  const isSaved = detail?.isSaved === 1;
  const followStatus = (detail?.topic?.followStatus ?? 0) as 0 | 1 | 2;
  return { title, summary, reportType, createdAt, isSaved, followStatus };
}

// ─────────────────────────────────────────────
// 主组件
// ─────────────────────────────────────────────
export function MarketInsightDetail({ useStore, onClose, className, showHeader }: MarketInsightDetailProps) {
  // ── 从 store 读取状态 ──
  const detail = useStore(s => s.detail);
  const saveLoading = useStore(s => s.saveLoading);
  const followLoading = useStore(s => s.followLoading);
  const detailLoading = useStore(s => s.detailLoading);
  const detailError = useStore(s => s.detailError);

  // 展示字段均从 detail 派生
  const { title, summary, reportType, createdAt, isSaved, followStatus } = useDetailFields(detail);

  // ── 从 store 读取 actions ──
  const toggleSave = useStore(s => s.toggleSave);
  const toggleFollow = useStore(s => s.toggleFollow);
  const fetchDetail = useStore(s => s.fetchDetail);

  const handleRetry = useCallback(() => {
    fetchDetail();
  }, [fetchDetail]);

  return (
    <MarketInsightDetailContent
      showHeader={showHeader}
      title={title}
      summary={summary}
      reportType={reportType}
      createdAt={createdAt}
      detail={detail}
      detailLoading={detailLoading}
      detailError={detailError}
      isSaved={isSaved}
      saveLoading={saveLoading}
      onToggleSave={toggleSave}
      followStatus={followStatus}
      followLoading={followLoading}
      onToggleFollow={toggleFollow}
      onClose={onClose}
      onRetry={handleRetry}
      className={className}
    />
  );
}

export type { MarketInsightDetailProps } from './types';
